/**
 * NotImplementedError — the throw payload that port stubs raise when a test
 * deliberately omits a port implementation (T016).
 *
 * Exists so the `PortErrorBoundary` (T022) and the future port-isolation
 * Playwright spec (Cluster 2 FR-054 assertion) can discriminate the
 * "port is not implemented" failure from every other render-time throw
 * — they check `err instanceof NotImplementedError && err.portName === 'content'`
 * (or similar) and render the boundary's fallback with the offending port
 * named in the diagnostic region.
 *
 * `portName` is a `readonly` instance field so the error carries the same
 * shape on every construction path. Captured via a plain assignment inside
 * the constructor rather than via `declare` + parameter property, because a
 * `.ts` module without experimental-decorators does not get the runtime
 * field allocation `declare` would otherwise imply. The field survives
 * minification — Biome / the repo's TypeScript config does not mangle
 * constructor-set instance fields.
 *
 * `Object.setPrototypeOf` restores the prototype chain after `super()` —
 * subclassing `Error` in ES2015+ transpiled-to-ES5-or-later output can drop
 * the subclass prototype, so `err instanceof NotImplementedError` silently
 * becomes false without this line. The project targets ES2023 so this is a
 * defence-in-depth move rather than a strict necessity today, but it keeps
 * the assertion contract robust across any future target downshift.
 *
 * Replacement-Path invariant: this class is the shared vocabulary between
 * the harness (throws), the boundary (catches + diagnoses), and the
 * port-isolation E2E (asserts fallback + port name). Changing the field
 * name or error-name string is a coordinated rename across all three.
 */

export class NotImplementedError extends Error {
  readonly portName: string;

  constructor(portName: string) {
    super(`Port "${portName}" is not implemented`);
    this.name = 'NotImplementedError';
    this.portName = portName;
    // Restore the prototype chain after `super()` so `instanceof` works
    // across transpile targets. See header comment.
    Object.setPrototypeOf(this, NotImplementedError.prototype);
  }
}
